import { ReactNode, useMemo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { usePathname, useRouter } from '@flipster/i18n/routing';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { css } from '@flipster/styled-system/css';
import segment from '@flipster/utils/segment';
import { HistoryEvent } from '@flipster/utils/segment/events/history';

import { useCheckSpotOnly } from '../../../../shared/regulation';
import { HistoryTabGroup, getHistoryTabList, getTabList } from '../../constants';

const Tabs = createTabsComponent<HistoryTabGroup>();
const SubTabs = createSingleChoice<string>();

interface Props {
  historyTabGroup: HistoryTabGroup;
  title: ReactNode;
}

const DesktopHistoryTab = ({ historyTabGroup, title }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const isSpotOnly = useCheckSpotOnly();
  const tabList = useMemo(() => getHistoryTabList(isSpotOnly), [isSpotOnly]);
  // 데스크톱 탭은 웹뷰에서 렌더되지 않지만, 목록 소스를 하나로 유지한다
  const subTabList = useMemo(
    () => getTabList(historyTabGroup, { isSpotOnly, isWebview: false }),
    [historyTabGroup, isSpotOnly],
  );

  return (
    <>
      <h1
        className={css({
          textStyle: 'Display/L',
          color: 'inverse.white',
          mb: 30,
          tabletDown: { display: 'none' },
        })}
      >
        <FormattedMessage id='Autogen_common.r251126.s_History' />
      </h1>
      <h2
        className={css({
          textStyle: 'Title/L',
          color: 'inverse.white',
          px: 16,
          mb: 24,
          tablet: { display: 'none' },
        })}
      >
        {title}
      </h2>
      <div className={css({ mb: 20, tabletDown: { display: 'none' } })}>
        <Tabs
          border
          type='responsive'
          value={historyTabGroup}
          onClick={(tab) => {
            // clicking selected tab again should clean all sub-filters.
            // using onChange prop blocks this feature.
            segment.track(HistoryEvent.HistoryTypeClick, { history_type: tab || '' });
            const tabs = getTabList(tab, { isSpotOnly, isWebview: false });
            router.push(tabs[0].value);
          }}
        >
          {tabList.map((tab) => (
            <Tabs.Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      </div>
      <div className={css({ tabletDown: { display: 'none' } })}>
        <SubTabs value={pathname}>
          {subTabList.map((tab) => (
            <SubTabs.Chip
              key={tab.value}
              value={tab.value}
              label={tab.label}
              // clicking selected chip again should clean all sub-filters.
              // using onChange prop on parent (Choice) blocks this feature.
              onClick={() => router.push(tab.value)}
            />
          ))}
        </SubTabs>
      </div>
    </>
  );
};

export default DesktopHistoryTab;
